<template>
  <QaPageShell :title="header.reason || $t('_QACaseDetail')" :loading="loading" :error="error" @retry="load">
    <template #toolbar>
      <v-btn variant="text" color="white" size="small" prepend-icon="mdi-arrow-left" @click="goBack">{{ $t('qaBack') }}</v-btn>
    </template>

    <!-- หัวเคส -->
    <v-card class="qa-card mt-3" variant="flat">
      <div class="d-flex flex-wrap align-center ga-5 pa-4">
        <!--
          Q9 — พื้นที่โดนัทมี 6 สถานะ ไม่ใช่แค่ "มีคะแนน"
          ⚠️ ห้ามโชว์ 0/100 แทนสถานะอื่น — 0 อ่านว่า "ได้ศูนย์คะแนน" ซึ่งร้ายแรงกับเจ้าหน้าที่
             ทั้งที่ความจริงคือ "ยังไม่มีคะแนน" · เคยยืมคะแนนจากเคสจำลองมาโชว์ตรงนี้ (borrowFor)
             ซึ่งแย่กว่า — ตัวเลขดูจริงแต่ไม่เกี่ยวกับเคสเลย
        -->
        <div class="qa-donut">
          <template v-if="evalStatus === 'ok'">
            <apexchart type="radialBar" height="150" width="150" :options="donutOptions" :series="[header.totalScore || 0]" />
            <div class="text-center mt-n2">
              <QaStatusChip :status="qaStatus" />
            </div>
          </template>

          <!-- เคสยังไม่ปิด — worker ทำงานหลังจบการสนทนาเท่านั้น (design: "ตอนนี้ประเมินหลังจบ case") -->
          <div v-else-if="isOpenCase" class="qa-donut-empty">
            <v-icon size="28" color="grey-lighten-1">mdi-chat-processing-outline</v-icon>
            <div class="text-caption text-medium-emphasis text-center mt-2">{{ $t('qaEvalAfterClose') }}</div>
          </div>

          <!-- เข้าคิวแล้ว — ไล่ถามผลอยู่เบื้องหลัง (ดู syncEvalPoll) -->
          <div v-else-if="evalStatus === 'pending'" class="qa-donut-empty">
            <v-progress-circular indeterminate size="36" width="3" color="primary" />
            <QaStatusChip status="pending" class="mt-2" />
          </div>

          <!-- ถูกข้ามตามเงื่อนไข pipeline — บอกเหตุผลให้เจ้าหน้าที่รู้ว่าเคสสั้นไม่ถูกนับ (ข้อจำกัด #1 ของแผน) -->
          <div v-else-if="evalStatus === 'skipped'" class="qa-donut-empty">
            <div class="qa-donut-dash qa-num">—</div>
            <QaStatusChip status="skipped" />
            <div class="text-caption text-medium-emphasis text-center mt-1">{{ skipReasonText }}</div>
          </div>

          <!-- failed / not-evaluated — สั่งประเมินใหม่ได้ (role 0,1 · เข้าคิวเป็น revision ใหม่) -->
          <div v-else class="qa-donut-empty">
            <div class="qa-donut-dash qa-num">—</div>
            <QaStatusChip :status="evalStatus === 'failed' ? 'failed' : 'not-evaluated'" />
            <div v-if="evalStatus === 'failed' && evalErrorText" class="text-caption text-error text-center mt-1">{{ evalErrorText }}</div>
            <v-btn
              v-if="canRequestEval"
              size="x-small"
              variant="tonal"
              color="primary"
              class="mt-2"
              :loading="requestingEval"
              prepend-icon="mdi-refresh"
              @click="requestEval"
            >{{ $t('qaReEvaluate') }}</v-btn>
            <div v-if="requestEvalError" class="text-caption text-error text-center mt-1">{{ requestEvalError }}</div>
          </div>
        </div>

        <div class="flex-grow-1" style="min-width: 260px">
          <div class="text-h6 font-weight-medium">{{ header.reason }}</div>
          <div class="d-flex align-center ga-2 flex-wrap mt-2">
            <QaChannelChip v-if="header.channel" :channel="header.channel" />
            <QaSocialChip v-if="header.channelId" :channel-id="header.channelId" />
            <!-- ⚠️ สายงานที่ใช้จัดคิวฝั่ง Salesforce · คนละเรื่องกับหัวข้อที่ AI อ่านจากบทสนทนา
                 ซ่อนเมื่อว่าง เพราะเคสที่เปิดตอนบอทคัดแยกปิดอยู่จะไม่มีค่านี้เลย -->
            <v-chip
              v-if="header.serviceType"
              size="small"
              label
              variant="outlined"
              class="text-medium-emphasis"
              :title="$t('qaServiceTypeHint')"
            >{{ header.serviceType }}</v-chip>
            <v-chip v-if="header.topicLabel" size="small" label variant="flat" :style="{ background: header.topicBg, color: header.topicColor }">{{ header.topicLabel }}</v-chip>
            <!-- R2 — ค่าจากข้อความแรกกับค่าที่สรุปแล้วอยู่ช่องเดียวกัน ต้องแยกให้เห็น -->
            <v-tooltip location="bottom" :text="classificationHint">
              <template #activator="{ props }">
                <v-chip
                  v-bind="props"
                  v-if="header.topicLabel"
                  size="x-small"
                  label
                  variant="tonal"
                  :color="header.isFinalClassification ? 'success' : 'warning'"
                  :prepend-icon="header.isFinalClassification ? 'mdi-check-decagram' : 'mdi-progress-clock'"
                >
                  {{ header.isFinalClassification ? 'สรุปแล้ว' : 'จากข้อความแรก' }}
                </v-chip>
              </template>
            </v-tooltip>
            <!--
              Q9 — ธง "ต้องให้คนดู" — คะแนนใบนี้ AI ไม่มั่นใจพอที่จะปล่อยผ่านเงียบ ๆ
              ⚠️ ต้องบอกเหตุผลใน tooltip เสมอ — chip เปล่า ๆ จะทำให้หัวหน้างานเดาไม่ถูก
                 ว่าต้องดูอะไร (ความมั่นใจต่ำ? เคสโอน?) แล้วก็จะเลิกกดดูไปเลย
            -->
            <v-tooltip v-if="showNeedsReview" location="bottom">
              <template #activator="{ props }">
                <v-chip
                  v-bind="props"
                  size="x-small"
                  label
                  variant="tonal"
                  color="warning"
                  prepend-icon="mdi-account-alert-outline"
                >{{ $t('qaNeedsHumanReview') }}</v-chip>
              </template>
              <div>
                <div v-for="r in reviewReasonTexts" :key="r" class="text-caption">• {{ r }}</div>
              </div>
            </v-tooltip>
            <!-- เวอร์ชัน rubric โชว์เฉพาะตอนมีคะแนนจริง — สถานะอื่นไม่มี rubric ผูกกับเคส -->
            <span class="text-caption text-medium-emphasis">
              เคส {{ header.caseNo }}<template v-if="evalStatus === 'ok' && rubricVersion"> · เกณฑ์ {{ rubricVersion }}</template>
            </span>
          </div>

          <v-row class="mt-2" dense>
            <v-col v-for="meta in metaRows" :key="meta.label" cols="6" sm="4" md="2">
              <div class="text-caption text-medium-emphasis">{{ meta.label }}</div>
              <div class="text-body-2" :class="meta.class">
                <a v-if="meta.link" class="text-primary qa-clickable" @click="openAgent">{{ meta.value }}</a>
                <span v-else>{{ meta.value }}</span>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <v-row class="mt-1" dense>
      <!--
        คะแนนรายหมวด + gates — โชว์เฉพาะตอน evalStatus === 'ok'
        ⚠️ สถานะอื่นซ่อนทั้ง column ไม่ใช่โชว์การ์ดว่าง — การ์ด "0/25 ทุกหัวข้อ" อ่านว่า
           เจ้าหน้าที่ทำแย่มาก ทั้งที่แค่ยังไม่มีการประเมิน
      -->
      <v-col v-if="showEvalDetails || evaluationLocked" cols="12" lg="7">
        <!-- role 2 ได้ 401/403 จาก endpoint evaluation — เห็นหัวเคสได้ แต่รายละเอียด
             (เหตุผล/หลักฐานที่ตัดจากบทสนทนาจริง) สิทธิ์แคบเท่า transcript -->
        <v-card v-if="evaluationLocked" class="qa-card" variant="flat">
          <div class="pa-6 text-center">
            <v-icon size="34" color="grey-lighten-1">mdi-lock-outline</v-icon>
            <div class="text-body-2 text-medium-emphasis mt-2">
              {{ evaluationError.httpStatus === 401 || evaluationError.httpStatus === 403
                ? $t('qaEvalForbidden')
                : $t('qaEvalUnavailable') }}
            </div>
          </div>
        </v-card>

        <template v-else>
          <v-card v-for="part in parts" :key="part.partNo" class="qa-card mb-3" variant="flat">
            <v-card-title class="d-flex align-center py-3">
              <v-avatar color="primary" size="24" rounded="lg" class="mr-2">
                <span class="text-caption text-white">{{ part.partNo }}</span>
              </v-avatar>
              <span class="text-subtitle-1">{{ part.titleTh || part.title }}</span>
              <v-spacer />
              <span class="text-body-1 font-weight-medium qa-num" :class="`text-${ratioColor(part.earned / part.max)}`">
                {{ part.earned }}<span class="text-medium-emphasis"> / {{ part.max }}</span>
              </span>
            </v-card-title>
            <v-divider />
            <div>
              <div v-for="c in part.criteria" :key="c.criteriaId" class="qa-criteria-row">
                <div class="qa-criteria-name">
                  <div class="text-body-2">{{ c.nameTh || c.name }}</div>
                  <div v-if="c.reason" class="text-caption mt-1" :class="c.earned == null ? 'text-medium-emphasis' : 'text-error'">{{ c.reason }}</div>
                </div>
                <!-- ⚠️ earned = null คือ "ไม่ได้ให้คะแนน/ไม่เกี่ยว — ถูกตัดออกจากตัวหารแล้ว"
                     ห้ามวาดเป็น 0/max สีแดง · 0 อ่านว่าเจ้าหน้าที่ทำแย่มาก ทั้งที่โมเดลแค่ไม่ตอบ -->
                <template v-if="c.earned == null">
                  <div></div>
                  <div class="qa-criteria-score text-caption text-medium-emphasis">
                    {{ c.notApplicable ? 'N/A · ไม่นับ' : 'ไม่ได้ให้คะแนน' }}
                  </div>
                </template>
                <template v-else>
                  <QaBar :percent="(c.earned / c.max) * 100" :show-value="false" />
                  <div class="qa-criteria-score qa-num" :class="`text-${ratioColor(c.earned / c.max)}`">
                    {{ c.earned }}<span class="text-medium-emphasis">/{{ c.max }}</span>
                  </div>
                </template>
              </div>
            </div>
          </v-card>

          <!-- Compliance gates -->
          <v-card class="qa-card" variant="flat">
            <v-card-title class="d-flex align-center py-3">
              <v-avatar color="indigo" size="24" rounded="lg" class="mr-2">
                <span class="text-caption text-white">4</span>
              </v-avatar>
              <span class="text-subtitle-1">{{ $t('qaComplianceGates') }}</span>
              <v-spacer />
              <span class="text-caption text-medium-emphasis">ผ่าน / ไม่ผ่าน · บังคับ</span>
            </v-card-title>
            <v-divider />
            <div class="pa-4">
              <!--
                มติ 24 ส.ค. 2026 — รอบนี้ยังไม่ตรวจ gates จริง (ทุก gate ผ่านเสมอ · gatesChecked=false)
                ⚠️ ต้องประกาศบนหน้าจอ ไม่ใช่ปล่อยให้ "ผ่านครบทุกเคส" ดูเป็นผลตรวจจริง —
                   หัวหน้างานจะเชื่อว่า compliance ถูกคุมแล้วทั้งที่ยังไม่มีใครตรวจเลย
                (alert "ตก gate = ตกทั้งเคส … ถูกส่งให้หัวหน้างานแล้ว" ของเดิมถูกถอด —
                 มันจะไม่มีวันขึ้นรอบนี้ และอ้างถึงกระบวนการส่งต่อที่ไม่มีอยู่จริง)
              -->
              <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                <span class="text-body-2">{{ $t('qaGatesNotChecked') }}</span>
              </v-alert>

              <div v-for="gate in gates" :key="gate.gateId" class="d-flex align-start mb-3">
                <v-avatar :color="gate.isPass ? 'success' : 'error'" variant="tonal" size="30" rounded="lg" class="mr-3">
                  <v-icon size="17" :color="gate.isPass ? 'success' : 'error'">{{ gate.isPass ? 'mdi-check' : 'mdi-close' }}</v-icon>
                </v-avatar>
                <div>
                  <div class="text-body-2 font-weight-medium">{{ gate.name }}</div>
                  <div class="text-caption" :class="gate.isPass ? 'text-success' : 'text-error'">{{ gate.isPass ? 'ผ่าน' : 'ไม่ผ่าน' }}</div>
                  <div v-if="gate.reason" class="text-caption text-medium-emphasis mt-1">{{ gate.reason }}</div>
                </div>
              </div>
            </div>
          </v-card>
        </template>
      </v-col>

      <!-- บทวิเคราะห์ + บทสนทนา -->
      <v-col cols="12" :lg="showEvalDetails || evaluationLocked ? 5 : 7">
        <v-card v-if="showEvalDetails" class="qa-card mb-3" variant="flat">
          <v-card-title class="d-flex align-center py-3">
            <v-icon color="primary" size="18" class="mr-2">mdi-creation-outline</v-icon>
            <span class="text-subtitle-1">{{ $t('qaAiAnalysis') }}</span>
          </v-card-title>
          <v-divider />
          <div class="pa-4">
            <div class="text-body-2">{{ analysis }}</div>

            <template v-if="heldBackReasons.length">
              <div class="text-subtitle-2 mt-4">{{ $t('qaReasonsHeld') }}</div>
              <div class="text-caption text-medium-emphasis mb-2">เหตุผลที่ AI หักคะแนนในหัวข้อเหล่านี้ — ใช้คุยกับเจ้าหน้าที่ได้เลย</div>
              <div v-for="(reason, i) in heldBackReasons" :key="reason.criteriaId" class="d-flex mb-3">
                <v-avatar color="error" variant="tonal" size="22" class="mr-3 mt-1">
                  <span class="text-caption">{{ i + 1 }}</span>
                </v-avatar>
                <div>
                  <div class="text-body-2 font-weight-medium">
                    {{ reason.name }}
                    <span class="text-caption text-medium-emphasis">· {{ reason.part }} · ได้ {{ reason.earned }}/{{ reason.max }}</span>
                  </div>
                  <div class="text-caption text-medium-emphasis">{{ reason.reason }}</div>
                </div>
              </div>
            </template>
            <div v-else class="text-body-2 text-success mt-3">{{ $t('qaNoPointsHeld') }}</div>
          </div>
        </v-card>

        <v-card class="qa-card" variant="flat">
          <v-card-title class="d-flex align-center py-3">
            <span class="text-subtitle-1">{{ $t('qaTranscriptExcerpt') }}</span>
            <v-spacer />
            <span class="text-caption text-medium-emphasis">{{ $t('qaFlaggedHint') }}</span>
          </v-card-title>
          <v-divider />
          <div v-if="transcriptError" class="pa-6 text-center">
            <v-icon size="34" color="grey-lighten-1">mdi-lock-outline</v-icon>
            <div class="text-body-2 text-medium-emphasis mt-2">
              {{ transcriptError.httpStatus === 401 || transcriptError.httpStatus === 403
                ? $t('qaTranscriptForbidden')
                : $t('qaTranscriptUnavailable') }}
            </div>
          </div>
          <div v-else-if="!transcript.length" class="pa-6 text-center text-body-2 text-medium-emphasis">
            {{ $t('qaNoData') }}
          </div>
          <div v-else class="qa-transcript">
            <!-- isFlagged มาจากชั้น service (จับ lineId เข้ากับ flaggedLineIds ของผลประเมิน) -->
            <div v-for="(line, i) in transcript" :key="i" class="qa-tx-line">
              <div class="qa-tx-who" :class="speakerClass(line.speaker)">{{ speakerLabel(line.speaker) }}</div>
              <div class="qa-tx-text" :class="{ 'qa-tx-flagged': line.isFlagged }">{{ line.text }}</div>
              <div class="qa-tx-time qa-num">{{ line.timestamp }}</div>
            </div>
          </div>
        </v-card>

        <!-- ประวัติการรับ-โอนเคส · มาจาก CONVERSATION_PARTICIPANT_CHANGED ของ Salesforce -->
        <v-card class="qa-card mt-3" variant="flat">
          <v-card-title class="d-flex align-center py-3">
            <span class="text-subtitle-1">{{ $t('qaTransferHistory') }}</span>
            <v-spacer />
            <span v-if="header.agentJoinCount" class="text-caption text-medium-emphasis">
              {{ $t('qaTransferCount') }}: {{ header.agentJoinCount }}
            </span>
          </v-card-title>
          <v-divider />
          <v-list v-if="participants.length" density="compact" class="py-1">
            <v-list-item v-for="(p, i) in participants" :key="i">
              <template #prepend>
                <v-icon :color="p.operation === 'add' ? 'success' : 'grey'" size="20">
                  {{ p.operation === 'add' ? 'mdi-account-arrow-right' : 'mdi-account-arrow-left' }}
                </v-icon>
              </template>
              <v-list-item-title class="text-body-2">
                {{ p.displayName }}
                <span class="text-caption text-medium-emphasis">
                  · {{ p.operation === 'add' ? $t('qaJoined') : $t('qaLeft') }} · {{ p.role }}
                </span>
              </v-list-item-title>
              <template #append>
                <span class="text-caption text-medium-emphasis qa-num">{{ formatDateTime(p.occurredAt) }}</span>
              </template>
            </v-list-item>
          </v-list>
          <div v-else class="pa-6 text-center text-body-2 text-medium-emphasis">
            {{ $t('qaNoTransferHistory') }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!--
      บันทึกผู้ช่วย AI — ซ้ายเป็นสรุปจำลอง · ขวาเป็นคำตอบที่แนะนำของจริง
      จากคิวเบื้องหลัง (recommend = null ได้ — เคสที่ยังไม่ถึงรอบแรก)
    -->
    <QaAssistPanel :assist="assist" :recommend="recommend || emptyRecommend" class="mt-3" />

    <QaSentimentPanel v-if="sentiment" :sentiment="sentiment" :channel="header.channel" class="mt-3" />
  </QaPageShell>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import Cookies from 'js-cookie';
import qaConsole, { getCaseRecommend } from '@/services/qaConsole';
import { formatDateTime, formatDuration } from '@/utils/qaFormat';
import { ratioColor } from '@/utils/qaScoring';
import QaPageShell from '@/components/QAConsole/QaPageShell.vue';
import QaStatusChip from '@/components/QAConsole/QaStatusChip.vue';
import QaChannelChip from '@/components/QAConsole/QaChannelChip.vue';
import QaSocialChip from '@/components/QAConsole/QaSocialChip.vue';
import QaBar from '@/components/QAConsole/QaBar.vue';
import QaAssistPanel from '@/components/QAConsole/QaAssistPanel.vue';
import QaSentimentPanel from '@/components/QAConsole/QaSentimentPanel.vue';

/**
 * Q9 — เหตุผลที่ pipeline ข้ามเคส (QaEvaluationWorker ประทับ skipReason เป็น enum)
 *
 * ⚠️ แปลที่นี่ ไม่ใช่ฝั่ง server — server เก็บ enum เพื่อให้ query/สถิติได้
 *    ส่วนคำอธิบายเป็นเรื่องหน้าจอล้วน ๆ (เหมือน statusLabel ใน qaFormat.js)
 * ⚠️ ต้องมีคำอธิบายเสมอ — "ข้าม" เฉย ๆ ทำให้เจ้าหน้าที่คิดว่าระบบพัง
 *    ทั้งที่เป็นเงื่อนไขที่ตั้งใจ (ข้อจำกัด #1 ของแผน: เคสสั้นไม่ถูกนับ)
 */
const SKIP_REASON_TH = {
  'no-agent-lines': 'บอทจบเองหรือเจ้าหน้าที่ตอบไม่พอ',
  'too-short': 'บทสนทนาสั้นกว่าเกณฑ์',
  'not-sampled': 'อยู่นอกกลุ่มสุ่มตรวจ',
  'excluded-agent': 'บัญชีทดสอบ',
  'empty-transcript': 'ไม่มีข้อความในบทสนทนา',
};

/**
 * Q9 — เหตุผลของธง "ต้องให้คนดู" (reviewReasons จาก G5 ของ worker)
 *
 * ⚠️ 'no-evidence' ต้องบอกว่า "(ไม่หัก)" — กติกาคือหักโดยไม่มีหลักฐานจะได้คะแนนเต็มคืน
 *    ถ้าไม่บอก หัวหน้างานจะเข้าใจว่าคะแนนถูกหักไปแล้วและไปตำหนิเจ้าหน้าที่ผิด ๆ
 */
const REVIEW_REASON_TH = {
  'low-confidence': 'ความมั่นใจต่ำ',
  'no-evidence': 'หักคะแนนโดยไม่มีหลักฐาน (ไม่หัก)',
  'model-skipped-criteria': 'โมเดลข้ามบางหัวข้อ',
  'transferred-case': 'เคสถูกโอน',
  'model-flagged': 'โมเดลขอให้คนตรวจ',
};

export default {
  name: 'QACaseDetail',
  components: { apexchart: VueApexCharts, QaPageShell, QaStatusChip, QaChannelChip, QaSocialChip, QaBar, QaAssistPanel, QaSentimentPanel },
  data() {
    return {
      loading: true,
      error: null,
      header: {},
      rubricVersion: '',
      parts: [],
      gates: [],
      analysis: '',
      heldBackReasons: [],
      /** ผลประเมินดิบจาก GET cases/{caseKey}/evaluation (null = ยังไม่มี / ไม่มีสิทธิ์) */
      evaluation: null,
      /** 401/403 จาก endpoint evaluation — สิทธิ์แคบเท่า transcript ห้ามพังทั้งหน้า */
      evaluationError: null,
      transcript: [],
      transcriptError: null,
      participants: [],
      sentiment: null,
      assist: null,
      recommend: null,
      /** ตัวจับเวลาไล่ถามคำแนะนำระหว่างที่ยังมีรอบค้างอยู่ (null = ไม่ได้ไล่ถามอยู่) */
      recommendPoll: null,
      /** ตัวจับเวลาไล่ถามผลประเมินตอน evalStatus = pending (null = ไม่ได้ไล่ถามอยู่) */
      evalPoll: null,
      /** นับรอบที่ถามไปแล้ว — เกินเพดานต้องหยุด ไม่ปล่อยยิงตลอดกะของเจ้าหน้าที่ */
      evalPollTries: 0,
      requestingEval: false,
      requestEvalError: null,
    };
  },
  computed: {
    /**
     * R2 — อธิบายว่าหัวข้อ/เรื่องที่ติดต่อบนหน้านี้มาจากไหน
     *
     * ⚠️ ต้องพูดถึง "ข้อความแรก" ให้ชัด · ทีม QA เอาค่านี้ไปทำสถิติ
     *    การไม่รู้ว่าค่าไหนยังเป็นการเดาอยู่ ทำให้สถิติผิดโดยไม่มีใครจับได้
     */
    classificationHint() {
      return this.header.isFinalClassification
        ? 'สรุปจากบทสนทนาทั้งหมดหลังเคสจบแล้ว'
        : 'ยังเป็นการจัดหมวดจากข้อความแรกของลูกค้า — ระบบจะสรุปใหม่อีกครั้งเมื่อเคสปิด';
    },

    caseNo() {
      return this.$route.query.caseNo;
    },
    /** เคสที่ยังไม่มีคำแนะนำ — แผงเดียวกันแต่ขึ้นสถานะว่ายังไม่ถึงรอบแรก */
    emptyRecommend() {
      return { rounds: [], turnCount: 0, everyNTurns: 0, isEnded: false };
    },
    /**
     * ผลตัดสิน pass/borderline/fail ของหัวเคส
     *
     * ⚠️ อ่าน qaStatus ก่อน status — ชั้น service ของจริงส่ง qaStatus (ตัดสินตอนอ่าน
     *    ด้วย policy ปัจจุบัน) · ส่วน status เป็นชื่อเก่าที่ฝั่ง mock ยังใช้อยู่
     */
    qaStatus() {
      return (this.header && (this.header.qaStatus || this.header.status)) || 'not-evaluated';
    },

    /**
     * สถานะการประเมิน — แกนของทุก element บนหน้านี้ (ตาราง §3.3 ของแผน)
     *
     * ⚠️ fallback ท้ายสุดมีไว้ให้เคสจำลอง (mock ไม่มี evalStatus แต่มีผลตัดสินครบ)
     *    เคสจริงได้ค่าจาก server เสมอ — ห้ามเดา ok จากการ "มี parts" เพราะชั้น service
     *    ส่ง parts ว่างมาได้ตอน role ไม่มีสิทธิ์
     */
    evalStatus() {
      if (this.evaluation && this.evaluation.evalStatus) return this.evaluation.evalStatus;
      if (this.header && this.header.evalStatus) return this.header.evalStatus;
      return ['pass', 'borderline', 'fail'].includes(this.qaStatus) ? 'ok' : 'not-evaluated';
    },

    /** เคสยังไม่ปิด — worker ยังไม่หยิบ ไม่ว่า evalStatus จะว่าอะไร */
    isOpenCase() {
      return !!this.header && this.header.caseStatus === 'open';
    },

    /** การ์ดรายละเอียดคะแนน (parts / gates / บทวิเคราะห์) โชว์เฉพาะตอนมีผลจริงและมีสิทธิ์อ่าน */
    showEvalDetails() {
      return this.evalStatus === 'ok' && !this.evaluationError;
    },

    evaluationLocked() {
      return !!this.evaluationError;
    },

    /**
     * ปุ่ม "ประเมินใหม่" — role 0,1 เท่านั้น (ตรงกับสิทธิ์ของ POST cases/{caseKey}/evaluate)
     *
     * ⚠️ cookie หายให้ถือเป็น '0' ตามแบบ NavBar (signalRService.connect ใช้ fallback เดียวกัน)
     *    การซ่อนปุ่มเป็นแค่ UX — เส้นกันจริงอยู่ที่ APIAuthen ฝั่ง server
     */
    canRequestEval() {
      if (typeof qaConsole.requestEvaluation !== 'function') return false;
      const role = Cookies.get('roleId') || '0';
      return role === '0' || role === '1';
    },

    /** ธง "ต้องให้คนดู" มีความหมายเฉพาะตอนมีคะแนนจริง — สถานะอื่นไม่มีอะไรให้คนดู */
    showNeedsReview() {
      if (this.evalStatus !== 'ok') return false;
      const flag = this.header && this.header.needsHumanReview;
      return !!(flag ?? (this.evaluation && this.evaluation.needsHumanReview));
    },

    reviewReasonTexts() {
      const reasons = (this.evaluation && this.evaluation.reviewReasons) || [];
      // role 2 ไม่เห็น evaluation — เห็นแค่ธงบนหัวเคส จึงบอกได้แค่ว่าควรให้หัวหน้างานดู
      if (!reasons.length) return ['ระบบแนะนำให้หัวหน้างานตรวจซ้ำ'];
      return reasons.map((r) => REVIEW_REASON_TH[r] || r);
    },

    skipReasonText() {
      const key = this.evaluation && this.evaluation.skipReason;
      return SKIP_REASON_TH[key] || 'ถูกข้ามตามเงื่อนไขที่ตั้งไว้ในแท็บ pipeline';
    },

    /** ข้อความ error ของรอบประเมินที่ล้มเหลว — มาจาก worker (เช่น output-truncated) */
    evalErrorText() {
      return (this.evaluation && this.evaluation.error) || '';
    },

    /** auto-fail — evaluation เป็นตัวจริง · header เป็นทางสำรองของ role ที่ไม่มีสิทธิ์อ่านรายละเอียด */
    hasAutoFail() {
      if (this.evaluation) return !!this.evaluation.hasAutoFail;
      return !!this.header.hasAutoFail;
    },

    /** ชื่อหัวข้อที่ทำให้ตกอัตโนมัติ — ว่างได้เมื่อ role ไม่มีสิทธิ์อ่าน parts */
    autoFailCriteriaName() {
      const id = this.evaluation && this.evaluation.autoFailCriteriaId;
      if (!id) return '';
      for (const p of this.parts) {
        const c = (p.criteria || []).find((x) => x.criteriaId === id);
        if (c) return c.nameTh || c.name || '';
      }
      return '';
    },

    metaRows() {
      // ⚠️ คะแนน/compliance เป็น "—" ทุกสถานะที่ไม่ใช่ ok — ห้ามโชว์ "0 / 100"
      //    (อ่านว่าได้ศูนย์) หรือ "ผ่านครบ" (อ่านว่าตรวจแล้ว) ทั้งที่ยังไม่มีการประเมิน
      const isOk = this.evalStatus === 'ok';
      return [
        { label: this.$t('qaAgent'), value: this.header.agentName, link: true, class: '' },
        { label: this.$t('qaDate'), value: formatDateTime(this.header.evaluatedAt), class: '' },
        { label: this.$t('qaDuration'), value: formatDuration(this.header.durationSec), class: 'qa-num' },
        { label: this.$t('qaQaScore'), value: isOk ? `${this.header.totalScore ?? 0} / 100` : '—', class: 'qa-num' },
        // gates ยังไม่ถูกตรวจรอบนี้ (ผ่านเสมอ) — เงื่อนไข "ตก gate" คงไว้เผื่อข้อมูลจำลองเก่า
        // auto-fail ต้องมองเห็นที่นี่ — เป็นทางเดียวที่เคสคะแนนสูงกลายเป็น "ไม่ผ่าน" ได้ในรอบนี้
        {
          label: this.$t('qaCompliance'),
          value: !isOk ? '—'
            : this.hasAutoFail ? ('ตกอัตโนมัติ' + (this.autoFailCriteriaName ? ` · ${this.autoFailCriteriaName}` : ''))
            : this.header.isGateFail ? 'ตก gate' : 'ผ่านครบ',
          class: !isOk ? '' : (this.hasAutoFail || this.header.isGateFail) ? 'text-error' : 'text-success',
        },
      ];
    },
    donutOptions() {
      const color = { pass: '#2E7D32', borderline: '#ED9C0E', fail: '#C62828' }[this.qaStatus] || '#1867C0';
      return {
        chart: { fontFamily: 'inherit', sparkline: { enabled: true } },
        colors: [color],
        plotOptions: {
          radialBar: {
            hollow: { size: '62%' },
            track: { background: '#eef0f2' },
            dataLabels: {
              name: { show: true, offsetY: 18, fontSize: '11px', color: '#6b7280', formatter: () => '/ 100' },
              value: { show: true, offsetY: -8, fontSize: '24px', fontWeight: 600, color, formatter: (v) => Math.round((v / 100) * 100) },
            },
          },
        },
        stroke: { lineCap: 'round' },
        labels: [''],
      };
    },
  },
  watch: {
    caseNo() {
      // เปลี่ยนเคส = เริ่มนับรอบไล่ถามใหม่ — ตัวเก่าผูกกับเพดานครั้งของเคสก่อนหน้า
      this.stopEvalPoll();
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  // ⚠️ ต้องหยุดตัวจับเวลาตอนออกจากหน้า ไม่งั้นมันไล่ถาม API ต่อไปเรื่อย ๆ ทั้งที่ไม่มีใครดู
  //    (และจะซ้อนกันหลายตัวถ้าเข้า-ออกหน้านี้หลายรอบ)
  beforeUnmount() {
    this.stopRecommendPoll();
    this.stopEvalPoll();
  },
  methods: {
    ratioColor,
    formatDateTime,
    /** transcript จริงมี 4 ฝั่ง (ลูกค้า / เจ้าหน้าที่ / บอท pre-chat / ระบบ) ไม่ใช่ 2 แบบ mock */
    speakerLabel(speaker) {
      if (speaker === 'agent') return this.$t('qaAgent');
      if (speaker === 'bot') return this.$t('qaBot');
      if (speaker === 'system') return this.$t('qaSystem');
      return this.$t('qaCustomer');
    },
    speakerClass(speaker) {
      if (speaker === 'agent') return 'text-primary';
      if (speaker === 'bot') return 'text-info';
      return 'text-medium-emphasis';
    },
    /**
     * @param quiet true = โหลดซ้ำเงียบ ๆ หลัง poll เจอผล — ไม่สลับไป skeleton
     *              เพราะหน้าจอมีข้อมูลครบอยู่แล้ว แค่ต้องเติมคะแนนที่เพิ่งมาถึง
     * ⚠️ เทียบ === true — เมธอดนี้ถูกผูกกับ @retry ซึ่งส่ง argument อื่นมาได้
     */
    async load(quiet = false) {
      if (!this.caseNo) {
        this.$router.replace({ name: 'QACases' });
        return;
      }
      if (quiet !== true) this.loading = true;
      this.error = null;
      try {
        const res = await qaConsole.getCaseDetail(this.caseNo);
        this.header = res.header;
        this.rubricVersion = res.rubricVersion;
        this.parts = res.parts;
        this.gates = res.gates;
        this.analysis = res.analysis;
        this.heldBackReasons = res.heldBackReasons;
        // Q9 — ผลประเมินดิบ (null = ยังไม่มี) · error เฉพาะส่วนแบบเดียวกับ transcript
        this.evaluation = res.evaluation || null;
        this.evaluationError = res.evaluationError || null;
        this.transcript = res.transcript;
        // ⚠️ transcript สิทธิ์แคบกว่าหัวเคส — role ที่ไม่มีสิทธิ์จะได้ error เฉพาะส่วนนี้
        //    ห้ามโยนขึ้นไปที่ this.error ไม่งั้นทั้งหน้าหายทั้งที่ข้อมูลอื่นอ่านได้
        this.transcriptError = res.transcriptError || null;
        this.participants = res.participants || [];
        this.sentiment = res.sentiment;
        this.assist = res.assist;
        // ⚠️ null ได้เป็นเรื่องปกติ — เคสเก่าที่ยังไม่เปิดฟีเจอร์ หรือคำถามยังไม่ถึงรอบแรก
        //    ตัวผสมกลืน error ของ endpoint นี้ไว้แล้ว (เหตุผลเดียวกับ transcript)
        this.recommend = res.recommend || null;
        this.syncRecommendPoll();
        this.syncEvalPoll();
      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    },
    /**
     * รอบที่ยัง "ไม่จบ" — ค่าที่หน้าจอยังต้องรอผล
     *
     * ⚠️ ต้องดูทั้ง queued และ running · ถ้าดูแต่ running จะพลาดช่วงที่งานยังไม่ถึงคิว
     *    (คิวเป็นเส้นเดียว บทสนทนาที่คุยกันเยอะอาจรอหลายสิบวินาที)
     */
    hasPendingRound() {
      const rounds = (this.recommend && this.recommend.rounds) || [];
      return rounds.some((r) => r.status === 'queued' || r.status === 'running');
    },

    /**
     * ไล่ถามคำแนะนำจนกว่ารอบจะจบ แล้วหยุดเอง
     *
     * ⚠️ ทำเพราะงานนี้เป็น **งานเบื้องหลัง** — ผลมาถึงหลังจากหน้าจอโหลดไปแล้วหลายวินาที
     *    ถ้าไม่ไล่ถาม วงหมุนจะค้างจนกว่าคนจะกด refresh เอง ซึ่งดูเหมือนระบบแฮงค์
     *
     * ⚠️ หยุดทันทีที่ไม่มีรอบค้าง — ไม่ปล่อยให้ยิงถี่ ๆ ตลอดเวลาที่เปิดหน้าไว้
     */
    syncRecommendPoll() {
      if (!this.hasPendingRound()) {
        this.stopRecommendPoll();
        return;
      }
      if (this.recommendPoll) return;

      this.recommendPoll = setInterval(async () => {
        try {
          // ⚠️ ถามด้วย caseNo ไม่ใช่ conversationId — ฝั่ง server แปลงคีย์ให้เองแล้ว
          //    และ conversationId ของเคสเปลี่ยนค่าได้ระหว่างทาง (ดู QaPendingCase)
          const fresh = await getCaseRecommend(this.caseNo);
          if (fresh) this.recommend = fresh;
        } catch {
          // อ่านไม่ได้รอบนี้ไม่ใช่เรื่องใหญ่ — ลองใหม่รอบหน้า ไม่ต้องรบกวนคนใช้
        }
        if (!this.hasPendingRound()) this.stopRecommendPoll();
      }, 4000);
    },

    stopRecommendPoll() {
      if (!this.recommendPoll) return;
      clearInterval(this.recommendPoll);
      this.recommendPoll = null;
    },

    /**
     * Q9 — ไล่ถามผลประเมินตอน pending (แบบเดียวกับ syncRecommendPoll แต่ช้ากว่า)
     *
     * ⚠️ 10 วิ ไม่ใช่ 4 วิ — งานประเมินยิง LLM ทั้งบทสนทนา ใช้เวลาเป็นสิบวินาทีต่อเคส
     *    และคิวมี MinGap คั่น · ถามถี่กว่านี้ได้แต่ pending ซ้ำ ๆ เปลือง request เปล่า
     * ⚠️ เพดาน 30 ครั้ง (~5 นาที) — เกินนั้นแปลว่างานติดอะไรสักอย่างฝั่ง worker
     *    ปล่อยวงหมุนค้างต่อมีแต่หลอกคนดูว่าใกล้เสร็จ · refresh เองเมื่อไหร่ก็เริ่มนับใหม่
     */
    syncEvalPoll() {
      if (this.evalStatus !== 'pending' || typeof qaConsole.getCaseEvaluation !== 'function') {
        this.stopEvalPoll();
        return;
      }
      if (this.evalPoll) return;

      this.evalPollTries = 0;
      this.evalPoll = setInterval(async () => {
        this.evalPollTries += 1;
        if (this.evalPollTries > 30) {
          this.stopEvalPoll();
          return;
        }
        try {
          const fresh = await qaConsole.getCaseEvaluation(this.caseNo);
          if (fresh && fresh.evalStatus && fresh.evalStatus !== 'pending') {
            this.stopEvalPoll();
            // โหลดทั้งหน้าใหม่แบบเงียบ — parts/gates/บรรทัดไฮไลต์/sentiment ถูกผูกกัน
            // ที่ชั้น service · เอาแค่ evaluation มาแปะเองจะได้หน้าที่ข้อมูลครึ่ง ๆ กลาง ๆ
            await this.load(true);
          }
        } catch {
          // อ่านไม่ได้รอบนี้ไม่ใช่เรื่องใหญ่ — ลองใหม่รอบหน้า (ยังอยู่ใต้เพดานครั้ง)
        }
      }, 10000);
    },

    stopEvalPoll() {
      if (!this.evalPoll) return;
      clearInterval(this.evalPoll);
      this.evalPoll = null;
    },

    /**
     * สั่งประเมินใหม่ — เข้าคิวเป็น revision ใหม่ (ของเก่าไม่หาย · คะแนนเดิมคือ
     * สิ่งที่เจ้าหน้าที่โต้แย้งได้ ห้ามลบทับ)
     */
    async requestEval() {
      this.requestingEval = true;
      this.requestEvalError = null;
      try {
        const res = await qaConsole.requestEvaluation(this.caseNo);
        // queued=false = ระบบปฏิเสธพร้อมเหตุผล (เช่น evaluator ปิดอยู่) — ไม่ใช่ error
        // ทาง HTTP จึงต้องอ่านจาก body แล้วบอกตรง ๆ ไม่สลับไป pending หลอก ๆ
        if (res && res.queued === false) {
          this.requestEvalError = res.reason || this.$t('qaRequestEvalFailed');
          return;
        }
        // สลับเป็น pending ทันทีโดยไม่รอโหลดใหม่ทั้งหน้า — งานเพิ่งเข้าคิว ผลยังไม่มา
        this.evaluation = { ...(this.evaluation || {}), evalStatus: 'pending' };
        this.header = { ...this.header, evalStatus: 'pending' };
        this.syncEvalPoll();
      } catch (err) {
        this.requestEvalError = (err && err.message) || this.$t('qaRequestEvalFailed');
      } finally {
        this.requestingEval = false;
      }
    },

    goBack() {
      const from = this.$route.query.from;
      const known = ['QAOverview', 'QAAgentDetail', 'QACases', 'QAInvestigations'];
      if (known.includes(from)) {
        const query = from === 'QAAgentDetail' ? { agentId: this.$route.query.agentId } : {};
        this.$router.push({ name: from, query });
        return;
      }
      this.$router.push({ name: 'QACases' });
    },
    openAgent() {
      this.$router.push({ name: 'QAAgentDetail', query: { agentId: this.header.agentId } });
    },
  },
};
</script>

<style scoped>
.qa-donut {
  width: 150px;
  flex: none;
}
/* สถานะที่ไม่มีคะแนน — กล่องสูงเท่าโดนัท (150px) กันหัวเคสกระโดดตอนสถานะเปลี่ยน */
.qa-donut-empty {
  height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.qa-donut-dash {
  font-size: 34px;
  line-height: 1;
  color: #9aa1a9;
  margin-bottom: 6px;
}
.qa-criteria-row {
  display: grid;
  grid-template-columns: minmax(160px, 1.4fr) minmax(90px, 1fr) 78px;
  gap: 14px;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #eef0f2;
}
.qa-criteria-row:last-child {
  border-bottom: none;
}
.qa-criteria-score {
  text-align: right;
  font-weight: 500;
}
.qa-transcript {
  max-height: 420px;
  overflow-y: auto;
  padding: 4px 0;
}
.qa-tx-line {
  display: grid;
  grid-template-columns: 74px 1fr 48px;
  gap: 10px;
  padding: 8px 16px;
  border-bottom: 1px solid #f4f6f8;
}
.qa-tx-who {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  padding-top: 2px;
}
.qa-tx-text {
  font-size: 13px;
  line-height: 1.6;
}
.qa-tx-flagged {
  background: #fdf3e0;
  border-left: 3px solid #ed9c0e;
  padding: 4px 8px;
  border-radius: 4px;
}
.qa-tx-time {
  font-size: 11px;
  color: #9aa1a9;
  text-align: right;
  padding-top: 3px;
}
@media (max-width: 600px) {
  .qa-criteria-row {
    grid-template-columns: 1fr 70px;
  }
  .qa-criteria-row > :nth-child(2) {
    display: none;
  }
}
</style>
